import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Portal } from 'react-portal';

import {
  SR_API_ACTION_INSURANCE_ENABLEMENT,
  SR_API_ACTION_INSURANCE_GET_PROPERTY_MANAGER_DETAILS,
  SR_API_ACTION_INSURANCE_GET_REFERRAL_LINKS,
} from 'api/utilityActionTypes';

import { stateOptionItems } from 'common/util/stateCodes';
import { TENANT_LIABILITY_REFERRAL_LINKS_ENABLED } from 'common/utility/constants';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_PARTNER_FORM_ADOBE } from 'client/components/SrFormikField';
import SrFilterCheckbox from 'client/components/SrFilterCheckbox';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

import { createFormValidationFn, validators } from 'client/helpers/formikHelpers';

import { buildEnablementPayload } from './enablementPayload';
import { getEnablementErrorMessage } from './enablementErrorMessage';

import './UPEnableTenantLiabilityModal.less';

// State options sorted alphabetically by name (the shared list leads with Texas).
const sortedStateOptionItems = [...stateOptionItems].sort((a, b) => a.label.localeCompare(b.label));

const getNameFields = ({ disabled }) => [
  {
    id: 'firstName',
    title: 'First Name',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter first name',
    },
    validations: ['required'],
  },
  {
    id: 'lastName',
    title: 'Last Name',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter last name',
    },
    validations: ['required'],
  },
];

const getContactFields = ({ disabled }) => [
  {
    id: 'phone',
    title: 'Phone Number',
    type: 'phone',
    properties: {
      disabled,
      placeholder: 'Enter phone number',
    },
    validations: ['required', 'phone'],
  },
  {
    id: 'email',
    title: 'Email',
    type: 'email',
    properties: {
      disabled,
      placeholder: 'Enter email address',
    },
    validations: ['required', 'email'],
  },
];

const getAddressFields = ({ disabled }) => [
  {
    id: 'legalName',
    title: 'Legal Company Name',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter legal company name',
    },
    validations: ['required'],
  },
  {
    id: 'addressLine1',
    title: 'Address Line 1',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter address line 1',
    },
    validations: ['required'],
  },
  {
    id: 'addressLine2',
    title: 'Address Line 2',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter address line 2 (optional)',
    },
    validations: [],
  },
];

const getCityStateZipFields = ({ disabled }) => [
  {
    id: 'city',
    title: 'City',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter city',
    },
    validations: ['required'],
  },
  {
    id: 'state',
    title: 'State',
    type: 'select',
    properties: {
      disabled,
      choices: sortedStateOptionItems,
      placeholder: 'Select state',
    },
    validations: ['required'],
  },
  {
    id: 'zip',
    title: 'ZIP Code',
    type: 'zipCode',
    properties: {
      disabled,
      placeholder: 'Enter ZIP code',
    },
    validations: ['required', 'zipCode'],
  },
];

// `required: true` is the submit-time variant for paths that must carry a referral
// ("Save & Enable" and exclusive submits); the rendered fields use the lenient
// mutual-requirement pair so partially-typed input errors read pairwise.
const getReferralFields = ({ disabled, required = false }) => [
  {
    id: 'providerName',
    title: 'Insurance Provider Name',
    type: 'short_text',
    properties: { disabled, placeholder: 'Geico' },
    validations: required ? ['required'] : ['requiredIfNotEmpty:referralUrl'],
  },
  {
    id: 'referralUrl',
    title: 'Referral Link',
    type: 'short_text',
    properties: { disabled, placeholder: 'https://geico.com' },
    validations: required
      ? ['required', 'httpsUrl']
      : ['requiredIfNotEmpty:providerName', 'httpsUrl'],
  },
];

// Renders nothing; runs the effect that forces the "exclusive" toggle back off
// whenever the referral link stops being valid (guards against enabling then
// clearing the link). Lives as a child so the hook can observe Formik values.
const ExclusiveAutoReset = ({ formikBag, isReferralValid }) => {
  const { providerName, referralUrl } = formikBag.values;
  useEffect(() => {
    if (formikBag.values.exclusive && !isReferralValid(formikBag.values)) {
      formikBag.setFieldValue('exclusive', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerName, referralUrl]);
  return null;
};

ExclusiveAutoReset.propTypes = {
  formikBag: PropTypes.object.isRequired,
  isReferralValid: PropTypes.func.isRequired,
};

const UPEnableTenantLiabilityModal = ({ utilityPartner, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  // The signed-in utility-profit user (the property manager in the self-service flow,
  // or the admin acting on their behalf). This is the "current user context" the modal
  // pre-fills from — note the consumer `currentUser` cursor is anonymous inside the
  // utility-profit platform, so we read `utilityUser` instead.
  const {
    utilityUserFirstName,
    utilityUserLastName,
    utilityUserEmail,
    utilityUserPhone,
  } = useBranch({
    utilityUserFirstName: ['utilityUser', 'firstName'],
    utilityUserLastName: ['utilityUser', 'lastName'],
    utilityUserEmail: ['utilityUser', 'email'],
    utilityUserPhone: ['utilityUser', 'phone'],
  });
  // FEAT-C85: referral links are hidden until the legacy renter flow can render them,
  // which collapses this wizard back to its single SimpleCover step. Everything that reads
  // this flag below is the step 2 machinery: the prefill fetch, the referral validation
  // paths, the provider fields and the exclusive toggle. The monorepo refuses the same
  // submissions server-side, so nothing here is the only thing standing in the way.
  const referralLinksEnabled = TENANT_LIABILITY_REFERRAL_LINKS_ENABLED;
  const [isLoading, setIsLoading] = useState(true);
  const [isReferralLoading, setIsReferralLoading] = useState(referralLinksEnabled);
  const [existingData, setExistingData] = useState(null);
  const [existingReferral, setExistingReferral] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [step, setStep] = useState(1);
  const hasFetchedRef = useRef(false);
  // Which footer button triggered the in-flight submit; Formik's onSubmit can't
  // otherwise tell "Save & Enable" (referral required) from "Skip" (no referral).
  const submitIntentRef = useRef('save');
  // Pending snap-back of the exclusive toggle after an invalid-input click.
  const exclusiveResetTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(exclusiveResetTimerRef.current), []);

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_INSURANCE_GET_PROPERTY_MANAGER_DETAILS,
      payload: {
        utilityPartnerId: utilityPartner.id,
      },
      onSuccess: ({ partnerInsuranceDetails }) => {
        setExistingData(partnerInsuranceDetails);
        setIsLoading(false);
      },
      onError: (error) => {
        // 404 means no existing data - this is expected for first-time enablement
        if (error.statusCode !== 404) {
          setFetchError(error);
        }
        setIsLoading(false);
      },
    });

    // Nothing to prefill while step 2 is hidden, and no reason to call an endpoint whose
    // result nobody can see.
    if (!referralLinksEnabled) return;

    // Prefill step 2 with the partner's saved referral link (if any). This fetch is
    // part of the loading gate: letting the user interact before it resolves risks
    // enableReinitialize wiping typed input, and lets ExclusiveAutoReset flip a
    // returning exclusive partner's toggle off before the prefill arrives.
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_INSURANCE_GET_REFERRAL_LINKS,
      payload: {
        utilityPartnerId: utilityPartner.id,
      },
      onSuccess: ({ referralLinks }) => {
        setExistingReferral((referralLinks && referralLinks[0]) || null);
        setIsReferralLoading(false);
      },
      onError: (error) => {
        // 404 means no saved referral link - expected for first-time enablement.
        if (error.statusCode !== 404) {
          setFetchError(error);
        }
        setIsReferralLoading(false);
      },
    });
  }, [dispatch, referralLinksEnabled, utilityPartner.id]);

  const sentinelRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(false);

  /*
   * Used to display a shadow effect when the user is at the top of the modal.
   * When the user starts scrolling and is about to reach the bottom, the shadow
   * disappear. It gives a visual impression that there's more content below.
   */
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAtBottom(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px -170px 0px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Both prefill fetches gate interaction: fields stay disabled and the wizard can't
  // advance until the details AND referral-link reads settle.
  const isFetching = isLoading || isReferralLoading;

  const nameFields = getNameFields({ disabled: isFetching });
  const contactFields = getContactFields({ disabled: isFetching });
  const addressFields = getAddressFields({ disabled: isFetching });
  const cityStateZipFields = getCityStateZipFields({ disabled: isFetching });
  const allFormFields = [...nameFields, ...contactFields, ...addressFields, ...cityStateZipFields];

  const referralFields = getReferralFields({ disabled: isFetching });
  const step1Fields = allFormFields; // existing name/contact/address/cityStateZip set

  const hasReferralInput = (values) =>
    Boolean((values.providerName || '').trim() || (values.referralUrl || '').trim());

  const isReferralValid = (values) => {
    const name = (values.providerName || '').trim();
    const url = (values.referralUrl || '').trim();
    // httpsUrl treats an empty string as valid (the field is optional on its own),
    // so require a non-empty url explicitly before the exclusive toggle can enable.
    return Boolean(name) && Boolean(url) && !validators.httpsUrl(url);
  };

  const handleSubmit = (values, { setSubmitting, setFieldError }) => {
    setSubmitError(null);
    // With step 2 hidden there is no referral input to read, and the stored exclusive flag
    // that seeds the toggle must not leak into the payload either, so every submission
    // takes the SimpleCover path.
    const exclusive = referralLinksEnabled && Boolean(values.exclusive);
    const hasReferral = referralLinksEnabled && hasReferralInput(values);
    const isUpdate = Boolean(existingData);
    // "Save & Enable" (and any exclusive submit) must carry a referral provider;
    // "Skip and enable SimpleCover" is the only no-referral path.
    const requireReferral =
      referralLinksEnabled && (exclusive || submitIntentRef.current === 'save');

    // Validate only the fields this submission path actually requires:
    // - a first-time non-exclusive submission must carry the company/legal details
    //   (exclusive skips them, and a returning partner already has them on file);
    // - a Save & Enable / exclusive submission must carry a valid provider name +
    //   https link (hard-required), and any partially-typed referral input must
    //   complete the name ⇔ link pair.
    const fieldsToValidate = [
      ...(exclusive || isUpdate ? [] : step1Fields),
      ...(requireReferral || hasReferral
        ? getReferralFields({ disabled: false, required: requireReferral })
        : []),
    ];
    const errors = createFormValidationFn(fieldsToValidate)(values);
    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([id, msg]) => setFieldError(id, msg));
      const referralHasError = referralLinksEnabled && referralFields.some((f) => errors[f.id]);
      const step1HasError = step1Fields.some((f) => errors[f.id]);
      // Keep the user on step 2 while the referral itself is invalid (that error is
      // only visible here); only once the referral is clean send them back to step 1
      // to fix the company/legal details.
      if (referralHasError) {
        setStep(2);
      } else if (!exclusive && step1HasError) {
        setStep(1);
      }
      setSubmitting(false);
      return;
    }

    const payload = {
      utilityPartnerId: utilityPartner.id,
      isUpdate,
      ...buildEnablementPayload({ values, exclusive, hasReferral, isUpdate }),
    };

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_INSURANCE_ENABLEMENT,
      payload,
      onSuccess: () => {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      },
      onError: (error) => {
        // Surface the failure on the step the submit buttons live on, with
        // partner-friendly copy instead of the backend's generic wrapper. That is step 2
        // normally, step 1 while referral links are hidden.
        if (referralLinksEnabled) {
          setStep(2);
        }
        setSubmitError(getEnablementErrorMessage(error, { exclusive }));
        setSubmitting(false);
      },
    });
  };

  const getInitialValues = () => {
    // Prefill the exclusive toggle from the stored flag (the source `insuranceStrategies`
    // is derived from: only `referralLink` ⇒ exclusive). Reading it here keeps a returning
    // exclusive partner's toggle on, so re-saving doesn't silently re-enable SimpleCover.
    const exclusive = Boolean(
      utilityPartner.experienceConfiguration?.tenantLiabilityPartnerLinkExclusive,
    );

    // Returning partners: source every field strictly from the saved insurance details
    // so we never overwrite a deliberately-blank saved value with a default.
    if (existingData) {
      return {
        firstName: existingData.userFirstName ?? '',
        lastName: existingData.userLastName ?? '',
        phone: existingData.legalPhone ?? '',
        email: existingData.legalEmail ?? '',
        legalName: existingData.legalName ?? '',
        addressLine1: existingData.street1 ?? '',
        addressLine2: existingData.street2 ?? '',
        city: existingData.city ?? '',
        state: existingData.state ?? '',
        zip: existingData.zip ?? '',
        providerName: existingReferral?.label ?? '',
        referralUrl: existingReferral?.url ?? '',
        exclusive,
      };
    }

    // First-time enablement: seed the representative from the signed-in utility-profit
    // user (their own name/email/phone), falling back to the partner's company contact
    // on file. Company name comes from the partner; company address isn't available
    // client-side, so those fields stay empty and editable.
    return {
      firstName: utilityUserFirstName || '',
      lastName: utilityUserLastName || '',
      phone:
        utilityUserPhone ||
        utilityPartner.companyPhone ||
        utilityPartner.customerSupportPhone ||
        '',
      email:
        utilityUserEmail ||
        utilityPartner.companyEmail ||
        utilityPartner.customerSupportEmail ||
        '',
      legalName: utilityPartner.name || '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      providerName: existingReferral?.label ?? '',
      referralUrl: existingReferral?.url ?? '',
      exclusive,
    };
  };

  if (fetchError) {
    return (
      <Portal>
        <SrBox
          alignItems="center"
          backgroundColor="rgba(25, 51, 64, 0.96)"
          bottom="0"
          display="flex"
          justifyContent="center"
          left="0"
          overflow="auto"
          position="fixed"
          right="0"
          top="0"
          zIndex="1000"
        >
          <SrBox
            alignItems="center"
            backgroundColor="white"
            borderRadius="14px"
            display="flex"
            flexDirection="column"
            gap="20px"
            padding="40px"
          >
            <SrText font="greycliff" color="red" weight="600" fontSize="18px">
              Failed to load data
            </SrText>
            <SrButton theme={SR_BUTTON_THEME_ADOBE_GREEN} onClick={onClose}>
              Close
            </SrButton>
          </SrBox>
        </SrBox>
      </Portal>
    );
  }

  return (
    <Formik enableReinitialize initialValues={getInitialValues()} onSubmit={handleSubmit}>
      {(formikBag) => (
        <Portal>
          <SrBox
            alignItems="flex-start"
            backgroundColor="rgba(25, 51, 64, 0.96)"
            bottom="0"
            display="flex"
            justifyContent="center"
            left="0"
            overflow="auto"
            padding="10vh 0"
            position="fixed"
            right="0"
            top="0"
            zIndex="1000"
          >
            <SrBox
              backgroundColor="#223C47"
              height="10vh"
              left="0"
              position="fixed"
              right="0"
              styleName="topOverlayMask"
              top="0"
              zIndex="10"
            />
            <SrBox
              backgroundColor="#223C47"
              bottom="0"
              height="10vh"
              left="0"
              position="fixed"
              right="0"
              styleName="bottomOverlayMask"
              zIndex="10"
            />
            <SrBox
              backgroundColor="white"
              borderRadius="14px"
              display="flex"
              flexDirection="column"
              maxWidth="600px"
              position="relative"
              width="100%"
            >
              {/* Header */}
              <SrBox
                alignItems="center"
                backgroundColor="white"
                borderRadius="14px 14px 0 0"
                display="flex"
                justifyContent="space-between"
                padding="40px 40px 24px"
                position="sticky"
                top="0"
                zIndex="1"
              >
                <SrFlex align="center" gap="12px">
                  {step === 2 && (
                    <button
                      aria-label="Back"
                      onClick={() => setStep(1)}
                      styleName="backButton"
                      type="button"
                    >
                      <svg
                        aria-hidden="true"
                        fill="none"
                        height="20"
                        viewBox="0 0 20 20"
                        width="20"
                      >
                        <path
                          d="M12.5 4 7 10l5.5 6"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        />
                      </svg>
                    </button>
                  )}
                  {step === 1 && (
                    <SrBox
                      backgroundImage="url(/images/simplecover-shield-icon.png)"
                      backgroundPosition="center"
                      backgroundRepeat="no-repeat"
                      backgroundSize="contain"
                      flexShrink="0"
                      height="32px"
                      width="32px"
                    />
                  )}
                  <SrText font="greycliff" color="blueGreycliff" weight="600" fontSize="26px">
                    {step === 1
                      ? 'Enable Tenant Liability Protection'
                      : 'Do you want to add your own insurance provider?'}
                  </SrText>
                </SrFlex>
                <button
                  aria-label="Close modal"
                  onClick={onClose}
                  styleName="closeButton"
                  type="button"
                >
                  &times;
                </button>
              </SrBox>

              {/* Content */}
              <SrBox backgroundColor="white" padding="0px 40px 0px">
                <Form className="form">
                  {step === 1 && (
                    <>
                      {/*
                       * Section order: Company Information renders first (the details we mostly
                       * already have on file), then the Company representative section last. This
                       * leads with company context and lets pre-filled fields carry most of the work.
                       */}
                      {/* Intro helper text: what enabling tenant liability does */}
                      <SrBox marginBottom="20px">
                        <SrText color="greyDark" font="greycliff" fontSize="14px" weight="500">
                          Tenant Liability Protection helps renters meet lease coverage requirements
                          through a master policy for the rental address. Confirm the company
                          details below to enable it.
                        </SrText>
                      </SrBox>

                      {/* Company Information (rendered first) */}
                      <SrFlex
                        align="center"
                        justify="between"
                        marginBottom="4px"
                        paddingRight="8px"
                      >
                        <SrText font="greycliff" color="blueGreycliff" weight="700" fontSize="18px">
                          Company Information
                        </SrText>
                        {isFetching && (
                          <SrFlex align="center" gap="4px">
                            <SrText
                              color="greyMonochromeInactive"
                              font="greycliff"
                              fontSize="14px"
                              weight="500"
                            >
                              Fetching details
                            </SrText>
                            <SrFlex align="center" gap="4px" marginLeft="2px">
                              <span styleName="dot" />
                              <span styleName="dot" />
                              <span styleName="dot" />
                            </SrFlex>
                          </SrFlex>
                        )}
                      </SrFlex>
                      <SrBox marginBottom="16px">
                        <SrText color="greyDark" font="greycliff" fontSize="14px" weight="500">
                          This legal company name and address will appear on tenant liability policy
                          documents.
                        </SrText>
                      </SrBox>
                      {addressFields.map((formField) => (
                        <SrBox key={formField.id} marginBottom="16px">
                          <SrFormikField
                            formField={formField}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                          />
                        </SrBox>
                      ))}
                      <SrFlex gap="16px" marginBottom="16px">
                        {cityStateZipFields.map((formField) => (
                          <SrBox key={formField.id} flex="1">
                            <SrFormikField
                              formField={formField}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                            />
                          </SrBox>
                        ))}
                      </SrFlex>

                      <SrBox height="1px" backgroundColor="#e8e8e8" margin="24px 0" />

                      {/* Company representative (rendered last) */}
                      <SrFlex
                        align="center"
                        justify="between"
                        marginBottom="4px"
                        paddingRight="8px"
                      >
                        <SrText font="greycliff" color="blueGreycliff" weight="700" fontSize="18px">
                          Company Representative
                        </SrText>
                      </SrFlex>
                      <SrBox marginBottom="16px">
                        <SrText color="greyDark" font="greycliff" fontSize="14px" weight="500">
                          This person will be listed as the primary contact for your company if
                          questions come up about coverage.
                        </SrText>
                      </SrBox>
                      <SrFlex gap="16px" marginBottom="16px">
                        {nameFields.map((formField) => (
                          <SrBox key={formField.id} flex="1">
                            <SrFormikField
                              formField={formField}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                            />
                          </SrBox>
                        ))}
                      </SrFlex>
                      {contactFields.map((formField) => (
                        <SrBox key={formField.id} marginBottom="16px">
                          <SrFormikField
                            formField={formField}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                          />
                        </SrBox>
                      ))}
                    </>
                  )}

                  {step === 2 && referralLinksEnabled && (
                    <>
                      <ExclusiveAutoReset formikBag={formikBag} isReferralValid={isReferralValid} />
                      <SrBox marginBottom="16px">
                        <SrText color="greyDark" font="greycliff" fontSize="14px" weight="500">
                          You can add your preferred liability provider for renters to view
                          alongside SimpleCover. If you do not add a provider, renters will only see
                          SimpleCover plans.
                        </SrText>
                      </SrBox>
                      {referralFields.map((formField) => (
                        <SrBox key={formField.id} marginBottom="16px">
                          <SrFormikField
                            formField={formField}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                          />
                        </SrBox>
                      ))}
                      <SrFlex align="flex-start" gap="8px" marginBottom="24px">
                        <SrBox marginTop="2px">
                          {/*
                           * The toggle stays clickable even without a valid provider name +
                           * https link: turning it on with invalid input surfaces the field
                           * errors (instead of a dead disabled control) and leaves it off.
                           */}
                          <SrFilterCheckbox
                            id="exclusive"
                            className="green"
                            value={Boolean(formikBag.values.exclusive)}
                            onChange={(_id, next) => {
                              if (next && !isReferralValid(formikBag.values)) {
                                // Let the switch flip on so the click visibly registers,
                                // then snap it back off once the field errors are showing.
                                // shouldValidate=false throughout: the form has no validate
                                // prop, so a validation pass would wipe the errors we set.
                                formikBag.setFieldValue('exclusive', true, false);
                                const errors = createFormValidationFn(
                                  getReferralFields({ disabled: false, required: true }),
                                )(formikBag.values);
                                Object.entries(errors).forEach(([fieldId, msg]) => {
                                  formikBag.setFieldTouched(fieldId, true, false);
                                  formikBag.setFieldError(fieldId, msg);
                                });
                                clearTimeout(exclusiveResetTimerRef.current);
                                exclusiveResetTimerRef.current = setTimeout(() => {
                                  formikBag.setFieldValue('exclusive', false, false);
                                }, 200);
                                return;
                              }
                              formikBag.setFieldValue('exclusive', next);
                            }}
                          />
                        </SrBox>
                        <SrBox>
                          {/* SrText is inline, so each line needs its own block wrapper. */}
                          <SrBox marginBottom="4px">
                            <SrText font="greycliff" weight="600" fontSize="14px">
                              Only show this provider to renters
                            </SrText>
                          </SrBox>
                          <SrText color="greyDark" font="greycliff" fontSize="14px" weight="500">
                            When this is on, renters will only see this provider. SimpleCover plans
                            will not be shown.
                          </SrText>
                          {!isReferralValid(formikBag.values) && (
                            <SrBox marginTop="4px">
                              <SrText
                                color="greyMonochromeInactive"
                                font="greycliff"
                                fontSize="14px"
                                weight="500"
                              >
                                Requires a provider name and a referral link starting with https://.
                              </SrText>
                            </SrBox>
                          )}
                        </SrBox>
                      </SrFlex>
                    </>
                  )}
                  <div ref={sentinelRef} />
                </Form>
              </SrBox>

              {/* Footer */}
              <SrBox
                backgroundColor="white"
                borderRadius="0 0 14px 14px"
                bottom="0"
                padding="16px 40px 40px"
                position="sticky"
                zIndex="1"
              >
                <SrBox
                  backdropFilter="blur(1px) saturate(100%)"
                  background="linear-gradient(to top, rgba(255,255,255,0.7), rgba(255,255,255,0))"
                  height="120px"
                  left="0"
                  pointerEvents="none"
                  position="absolute"
                  right="0"
                  top="-120px"
                  visibility={isAtBottom ? 'hidden' : 'visible'}
                  zIndex="1"
                />
                {submitError && (
                  <SrBox marginBottom="16px">
                    <SrErrorMessage>{submitError}</SrErrorMessage>
                  </SrBox>
                )}
                {step === 1 &&
                  (referralLinksEnabled ? (
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_GREEN}
                      width="100%"
                      disabled={isFetching}
                      onClick={() => setStep(2)}
                    >
                      Continue
                    </SrButton>
                  ) : (
                    // Referral links hidden: step 1 is the whole modal, so it submits
                    // SimpleCover directly instead of advancing the wizard.
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_GREEN}
                      width="100%"
                      disabled={isFetching || formikBag.isSubmitting}
                      onClick={() => {
                        submitIntentRef.current = 'skip';
                        formikBag.submitForm();
                      }}
                    >
                      Save &amp; Enable
                    </SrButton>
                  ))}
                {step === 2 && referralLinksEnabled && (
                  <SrFlex gap="12px">
                    <SrBox flex="3 1 0%">
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREY}
                        width="100%"
                        disabled={
                          isFetching ||
                          hasReferralInput(formikBag.values) ||
                          Boolean(formikBag.values.exclusive) ||
                          formikBag.isSubmitting
                        }
                        onClick={() => {
                          submitIntentRef.current = 'skip';
                          formikBag.submitForm();
                        }}
                      >
                        Skip and enable SimpleCover
                      </SrButton>
                    </SrBox>
                    <SrBox flex="2 1 0%">
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        width="100%"
                        disabled={isFetching || formikBag.isSubmitting}
                        onClick={() => {
                          submitIntentRef.current = 'save';
                          formikBag.submitForm();
                        }}
                      >
                        Save &amp; Enable
                      </SrButton>
                    </SrBox>
                  </SrFlex>
                )}
              </SrBox>
            </SrBox>
          </SrBox>
        </Portal>
      )}
    </Formik>
  );
};

UPEnableTenantLiabilityModal.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

UPEnableTenantLiabilityModal.displayName = 'UPEnableTenantLiabilityModal';

export default UPEnableTenantLiabilityModal;
